/**
 * Vehicle Service
 * Járművek, szervizek és költségek kezelése Firestore-ban
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  getDoc,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  DocumentData,
  QueryDocumentSnapshot,
  QueryConstraint
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import {
  Vehicle,
  VehicleWithFieldValues,
  VehicleService,
  VehicleServiceWithFieldValues,
  VehicleExpense,
  VehicleExpenseWithFieldValues,
  VehicleStats,
  FleetDashboard,
  VehicleFormData
} from '../types/vehicle';

const VEHICLES_COLLECTION = 'vehicles';
const VEHICLE_SERVICES_COLLECTION = 'vehicleServices';
const VEHICLE_EXPENSES_COLLECTION = 'vehicleExpenses';

// Helper function to convert Timestamp to Date
const toDate = (date: any): Date => {
  if (!date) return new Date();
  if (date.toDate && typeof date.toDate === 'function') {
    return date.toDate();
  }
  if (date instanceof Date) return date;
  return new Date(date);
};

// ============================================================================
// VEHICLES - CRUD Operations
// ============================================================================

/**
 * Összes jármű lekérése egy szervezethez
 */
export async function getVehicles(
  organizationId: string,
  status?: string
): Promise<Vehicle[]> {
  try {
    const constraints: QueryConstraint[] = [
      where('organizationId', '==', organizationId),
      orderBy('licensePlate', 'asc')
    ];

    if (status) {
      constraints.push(where('status', '==', status));
    }

    const q = query(collection(db, VEHICLES_COLLECTION), ...constraints);
    const snapshot = await getDocs(q);

    const vehicles = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date(),
      purchaseDate: doc.data().purchaseDate?.toDate(),
      nextServiceDate: doc.data().nextServiceDate?.toDate(),
      insuranceExpiryDate: doc.data().insuranceExpiryDate?.toDate(),
      inspectionExpiryDate: doc.data().inspectionExpiryDate?.toDate(),
      documents: doc.data().documents?.map((d: any) => ({
        ...d,
        uploadedAt: d.uploadedAt?.toDate() || new Date(),
        expiryDate: d.expiryDate?.toDate()
      })) || [],
      photos: doc.data().photos?.map((p: any) => ({
        ...p,
        uploadedAt: p.uploadedAt?.toDate() || new Date()
      })) || []
    })) as Vehicle[];

    // Denormalizáljuk a hiányzó customerName mezőket
    const vehiclesWithNames = await Promise.all(
      vehicles.map(async (vehicle) => {
        // Ha van customerId de nincs customerName, lekérjük
        if (vehicle.customerId && !vehicle.customerName) {
          try {
            const orgDoc = await getDoc(doc(db, 'organizations', vehicle.customerId));
            if (orgDoc.exists()) {
              return {
                ...vehicle,
                customerName: orgDoc.data().name
              };
            }
          } catch (error) {
            console.error('Error fetching organization name for vehicle:', vehicle.id, error);
          }
        }
        return vehicle;
      })
    );

    return vehiclesWithNames;
  } catch (error) {
    console.error('Error fetching vehicles:', error);
    throw error;
  }
}

/**
 * Egy jármű lekérése ID alapján
 */
export async function getVehicle(vehicleId: string): Promise<Vehicle | null> {
  try {
    const docRef = doc(db, VEHICLES_COLLECTION, vehicleId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    const data = docSnap.data();
    return {
      id: docSnap.id,
      ...data,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      purchaseDate: data.purchaseDate?.toDate(),
      nextServiceDate: data.nextServiceDate?.toDate(),
      insuranceExpiryDate: data.insuranceExpiryDate?.toDate(),
      inspectionExpiryDate: data.inspectionExpiryDate?.toDate(),
      documents: data.documents?.map((d: any) => ({
        ...d,
        uploadedAt: d.uploadedAt?.toDate() || new Date(),
        expiryDate: d.expiryDate?.toDate()
      })) || [],
      photos: data.photos?.map((p: any) => ({
        ...p,
        uploadedAt: p.uploadedAt?.toDate() || new Date()
      })) || []
    } as Vehicle;
  } catch (error) {
    console.error('Error fetching vehicle:', error);
    throw error;
  }
}

/**
 * Új jármű létrehozása
 */
export async function createVehicle(
  vehicleData: VehicleFormData,
  organizationId: string,
  userId: string
): Promise<string> {
  try {
    // Undefined értékek kiszűrése - Firestore nem támogatja az undefined-ot
    const cleanedData: any = {};
    Object.entries(vehicleData).forEach(([key, value]) => {
      if (value !== undefined) {
        cleanedData[key] = value;
      }
    });

    // Ha van customerId, lekérjük a szervezet nevét denormalizáláshoz
    let customerName: string | undefined;
    if (vehicleData.customerId) {
      try {
        const orgDoc = await getDoc(doc(db, 'organizations', vehicleData.customerId));
        if (orgDoc.exists()) {
          customerName = orgDoc.data().name;
        }
      } catch (error) {
        console.error('Error fetching organization name:', error);
      }
    }

    const newVehicle: VehicleWithFieldValues = {
      ...cleanedData,
      licensePlate: vehicleData.licensePlate.toUpperCase(),
      customerName, // Denormalizált szervezet név
      documents: [],
      photos: [],
      organizationId,
      createdBy: userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, VEHICLES_COLLECTION), newVehicle);
    return docRef.id;
  } catch (error) {
    console.error('Error creating vehicle:', error);
    throw error;
  }
}

/**
 * Jármű frissítése
 * Megjegyzés: Ez a függvény bármilyen Vehicle mezőt frissíthet, nem csak VehicleFormData mezőket
 */
export async function updateVehicle(
  vehicleId: string,
  updates: Partial<Omit<Vehicle, 'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'organizationId' | 'documents' | 'photos'>>
): Promise<void> {
  try {
    const docRef = doc(db, VEHICLES_COLLECTION, vehicleId);
    
    // Undefined értékek kiszűrése - Firestore nem támogatja az undefined-ot
    const cleanedUpdates: any = {};
    Object.entries(updates).forEach(([key, value]) => {
      if (value !== undefined) {
        cleanedUpdates[key] = value;
      }
    });

    // Ha van customerId változás, lekérjük a szervezet nevét denormalizáláshoz
    if (updates.customerId !== undefined) {
      if (updates.customerId) {
        try {
          const orgDoc = await getDoc(doc(db, 'organizations', updates.customerId));
          if (orgDoc.exists()) {
            cleanedUpdates.customerName = orgDoc.data().name;
          }
        } catch (error) {
          console.error('Error fetching organization name:', error);
        }
      } else {
        // Ha customerId törlődik, töröljük a customerName-et is
        cleanedUpdates.customerName = null;
      }
    }

    const updateData: any = {
      ...cleanedUpdates,
      updatedAt: serverTimestamp()
    };

    if (updates.licensePlate) {
      updateData.licensePlate = updates.licensePlate.toUpperCase();
    }

    await updateDoc(docRef, updateData);
  } catch (error) {
    console.error('Error updating vehicle:', error);
    throw error;
  }
}

/**
 * Jármű törlése
 */
export async function deleteVehicle(vehicleId: string): Promise<void> {
  try {
    const docRef = doc(db, VEHICLES_COLLECTION, vehicleId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting vehicle:', error);
    throw error;
  }
}

/**
 * Km óra frissítése
 */
export async function updateVehicleMileage(
  vehicleId: string,
  mileage: number
): Promise<void> {
  try {
    const docRef = doc(db, VEHICLES_COLLECTION, vehicleId);
    await updateDoc(docRef, {
      mileage,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating vehicle mileage:', error);
    throw error;
  }
}

// ============================================================================
// VEHICLE SERVICES - CRUD Operations
// ============================================================================

/**
 * Jármű szerviz előzmények lekérése
 */
export async function getVehicleServices(
  vehicleId: string,
  limitCount?: number
): Promise<VehicleService[]> {
  try {
    const constraints: QueryConstraint[] = [
      where('vehicleId', '==', vehicleId),
      orderBy('date', 'desc')
    ];

    if (limitCount) {
      constraints.push(limit(limitCount));
    }

    const q = query(collection(db, VEHICLE_SERVICES_COLLECTION), ...constraints);
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      date: doc.data().date?.toDate() || new Date(),
      nextServiceDate: doc.data().nextServiceDate?.toDate(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date(),
      documents: doc.data().documents?.map((d: any) => ({
        ...d,
        uploadedAt: d.uploadedAt?.toDate() || new Date()
      })) || []
    })) as VehicleService[];
  } catch (error) {
    console.error('Error fetching vehicle services:', error);
    throw error;
  }
}

/**
 * Szervezet összes szerviz előzménye
 */
export async function getOrganizationServices(
  organizationId: string,
  limitCount?: number
): Promise<VehicleService[]> {
  try {
    const constraints: QueryConstraint[] = [
      where('organizationId', '==', organizationId),
      orderBy('date', 'desc')
    ];

    if (limitCount) {
      constraints.push(limit(limitCount));
    }

    const q = query(collection(db, VEHICLE_SERVICES_COLLECTION), ...constraints);
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      date: doc.data().date?.toDate() || new Date(),
      nextServiceDate: doc.data().nextServiceDate?.toDate(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date(),
      documents: doc.data().documents?.map((d: any) => ({
        ...d,
        uploadedAt: d.uploadedAt?.toDate() || new Date()
      })) || []
    })) as VehicleService[];
  } catch (error) {
    console.error('Error fetching organization services:', error);
    throw error;
  }
}

/**
 * Új szerviz bejegyzés létrehozása
 */
export async function createVehicleService(
  serviceData: Omit<VehicleService, 'id' | 'createdAt' | 'updatedAt'>,
  organizationId: string,
  userId: string
): Promise<string> {
  try {
    const newService: VehicleServiceWithFieldValues = {
      ...serviceData,
      organizationId,
      createdBy: userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, VEHICLE_SERVICES_COLLECTION), newService);

    // Jármű következő szerviz dátumának frissítése
    if (serviceData.nextServiceDate || serviceData.nextServiceMileage) {
      await updateVehicle(serviceData.vehicleId, {
        nextServiceDate: serviceData.nextServiceDate as Date,
        nextServiceMileage: serviceData.nextServiceMileage
      });
    }

    // Km óra frissítése a járművön
    await updateVehicleMileage(serviceData.vehicleId, serviceData.mileage);

    return docRef.id;
  } catch (error) {
    console.error('Error creating vehicle service:', error);
    throw error;
  }
}

/**
 * Szerviz bejegyzés frissítése
 */
export async function updateVehicleService(
  serviceId: string,
  updates: Partial<VehicleService>
): Promise<void> {
  try {
    const docRef = doc(db, VEHICLE_SERVICES_COLLECTION, serviceId);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating vehicle service:', error);
    throw error;
  }
}

/**
 * Szerviz bejegyzés törlése
 */
export async function deleteVehicleService(serviceId: string): Promise<void> {
  try {
    const docRef = doc(db, VEHICLE_SERVICES_COLLECTION, serviceId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting vehicle service:', error);
    throw error;
  }
}

// ============================================================================
// VEHICLE EXPENSES - CRUD Operations
// ============================================================================

/**
 * Jármű költségek lekérése
 */
export async function getVehicleExpenses(
  vehicleId: string,
  limitCount?: number
): Promise<VehicleExpense[]> {
  try {
    const constraints: QueryConstraint[] = [
      where('vehicleId', '==', vehicleId),
      orderBy('date', 'desc')
    ];

    if (limitCount) {
      constraints.push(limit(limitCount));
    }

    const q = query(collection(db, VEHICLE_EXPENSES_COLLECTION), ...constraints);
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      date: doc.data().date?.toDate() || new Date(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date()
    })) as VehicleExpense[];
  } catch (error) {
    console.error('Error fetching vehicle expenses:', error);
    throw error;
  }
}

/**
 * Szervezet összes költsége
 */
export async function getOrganizationExpenses(
  organizationId: string,
  limitCount?: number
): Promise<VehicleExpense[]> {
  try {
    const constraints: QueryConstraint[] = [
      where('organizationId', '==', organizationId),
      orderBy('date', 'desc')
    ];

    if (limitCount) {
      constraints.push(limit(limitCount));
    }

    const q = query(collection(db, VEHICLE_EXPENSES_COLLECTION), ...constraints);
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      date: doc.data().date?.toDate() || new Date(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date()
    })) as VehicleExpense[];
  } catch (error) {
    console.error('Error fetching organization expenses:', error);
    throw error;
  }
}

/**
 * Új költség bejegyzés létrehozása
 */
export async function createVehicleExpense(
  expenseData: Omit<VehicleExpense, 'id' | 'createdAt' | 'updatedAt'>,
  organizationId: string,
  userId: string
): Promise<string> {
  try {
    const newExpense: VehicleExpenseWithFieldValues = {
      ...expenseData,
      organizationId,
      createdBy: userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, VEHICLE_EXPENSES_COLLECTION), newExpense);

    // Ha van km óra adat, frissítsük a járművön
    if (expenseData.mileage) {
      await updateVehicleMileage(expenseData.vehicleId, expenseData.mileage);
    }

    return docRef.id;
  } catch (error) {
    console.error('Error creating vehicle expense:', error);
    throw error;
  }
}

/**
 * Költség bejegyzés frissítése
 */
export async function updateVehicleExpense(
  expenseId: string,
  updates: Partial<VehicleExpense>
): Promise<void> {
  try {
    const docRef = doc(db, VEHICLE_EXPENSES_COLLECTION, expenseId);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating vehicle expense:', error);
    throw error;
  }
}

/**
 * Költség bejegyzés törlése
 */
export async function deleteVehicleExpense(expenseId: string): Promise<void> {
  try {
    const docRef = doc(db, VEHICLE_EXPENSES_COLLECTION, expenseId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting vehicle expense:', error);
    throw error;
  }
}

// ============================================================================
// STATISTICS & DASHBOARD
// ============================================================================

/**
 * Flotta statisztikák számítása
 */
export async function getVehicleStats(organizationId: string): Promise<VehicleStats> {
  try {
    const vehicles = await getVehicles(organizationId);
    const services = await getOrganizationServices(organizationId);
    const expenses = await getOrganizationExpenses(organizationId);

    const activeVehicles = vehicles.filter(v => v.status === 'active').length;
    const inMaintenanceVehicles = vehicles.filter(v => v.status === 'maintenance').length;
    const totalMileage = vehicles.reduce((sum, v) => sum + v.mileage, 0);
    const averageMileage = vehicles.length > 0 ? totalMileage / vehicles.length : 0;

    const totalServiceCost = services.reduce((sum, s) => sum + s.totalCost, 0);
    const totalExpenses = expenses.reduce((sum, e) => sum + e.amount, 0);

    // Aktuális hónap költségei
    const now = new Date();
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const monthlyExpenses = expenses
      .filter(e => toDate(e.date) >= firstDayOfMonth)
      .reduce((sum, e) => sum + e.amount, 0);

    // Közelgő szervizek (30 napon belül)
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    const upcomingServices = vehicles.filter(v => 
      v.nextServiceDate && toDate(v.nextServiceDate) <= thirtyDaysFromNow
    ).length;

    // Lejáró dokumentumok (30 napon belül)
    const expiringDocuments = vehicles.filter(v =>
      (v.insuranceExpiryDate && toDate(v.insuranceExpiryDate) <= thirtyDaysFromNow) ||
      (v.inspectionExpiryDate && toDate(v.inspectionExpiryDate) <= thirtyDaysFromNow)
    ).length;

    return {
      totalVehicles: vehicles.length,
      activeVehicles,
      inMaintenanceVehicles,
      totalMileage,
      averageMileage: Math.round(averageMileage),
      totalServiceCost,
      totalExpenses,
      monthlyExpenses,
      upcomingServices,
      expiringDocuments
    };
  } catch (error) {
    console.error('Error calculating vehicle stats:', error);
    throw error;
  }
}

/**
 * Flotta dashboard adatok
 */
export async function getFleetDashboard(organizationId: string): Promise<FleetDashboard> {
  try {
    const stats = await getVehicleStats(organizationId);
    const recentServices = await getOrganizationServices(organizationId, 10);
    const recentExpenses = await getOrganizationExpenses(organizationId, 10);
    
    const vehicles = await getVehicles(organizationId);
    const now = new Date();
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

    const upcomingReminders: FleetDashboard['upcomingReminders'] = [];

    vehicles.forEach(vehicle => {
      if (vehicle.nextServiceDate && toDate(vehicle.nextServiceDate) <= thirtyDaysFromNow) {
        const daysUntil = Math.ceil((toDate(vehicle.nextServiceDate).getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        upcomingReminders.push({
          vehicleId: vehicle.id,
          licensePlate: vehicle.licensePlate,
          type: 'service',
          date: toDate(vehicle.nextServiceDate),
          daysUntil
        });
      }

      if (vehicle.insuranceExpiryDate && toDate(vehicle.insuranceExpiryDate) <= thirtyDaysFromNow) {
        const daysUntil = Math.ceil((toDate(vehicle.insuranceExpiryDate).getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        upcomingReminders.push({
          vehicleId: vehicle.id,
          licensePlate: vehicle.licensePlate,
          type: 'insurance',
          date: toDate(vehicle.insuranceExpiryDate),
          daysUntil
        });
      }

      if (vehicle.inspectionExpiryDate && toDate(vehicle.inspectionExpiryDate) <= thirtyDaysFromNow) {
        const daysUntil = Math.ceil((toDate(vehicle.inspectionExpiryDate).getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        upcomingReminders.push({
          vehicleId: vehicle.id,
          licensePlate: vehicle.licensePlate,
          type: 'inspection',
          date: toDate(vehicle.inspectionExpiryDate),
          daysUntil
        });
      }
    });

    // Rendezés dátum szerint
    upcomingReminders.sort((a, b) => a.daysUntil - b.daysUntil);

    return {
      stats,
      recentServices,
      recentExpenses,
      upcomingReminders
    };
  } catch (error) {
    console.error('Error fetching fleet dashboard:', error);
    throw error;
  }
}
